import { Check } from "@untitledui/icons";
import { Link } from "react-router-dom";
import { Button } from "@/components/base/buttons/button";

interface Plan {
  name: string;
  description: string;
  price: string;
  period: string;
  features: string[];
  cta: string;
  popular: boolean;
}

const plans: Plan[] = [
  {
    name: "Starter",
    description: "For solo mechanics and micro garages",
    price: "€9",
    period: "per month",
    features: [
      "2 users",
      "150 customers & vehicles",
      "Unlimited jobs & invoices",
      "All reports + CSV export",
      "Appointment scheduling",
      "Email support",
    ],
    cta: "Start 14-Day Free Trial",
    popular: true,
  },
  {
    name: "Pro",
    description: "For small garages with a growing team",
    price: "€24",
    period: "per month",
    features: [
      "5 users, 4 roles",
      "Unlimited customers & vehicles",
      "Inventory tracking",
      "25 storage units",
      "All reports + Print",
      "Priority support",
    ],
    cta: "Start 14-Day Free Trial",
    popular: false,
  },
  {
    name: "Business",
    description: "For established garages that need it all",
    price: "€49",
    period: "per month",
    features: [
      "15 users, all 6 roles",
      "Unlimited everything",
      "Full inventory management",
      "Custom logo & branding",
      "Unlimited storage",
      "Priority support (12h)",
    ],
    cta: "Start 14-Day Free Trial",
    popular: false,
  },
];

export default function Packages() {
  return (
    <section id="pricing" className="section-padding bg-bg-secondary">
      <div className="landing-container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="heading-2 text-text-primary mb-4">
            Simple Pricing,{" "}
            <span className="text-gradient">No Surprises</span>
          </h2>
          <p className="subtitle-2">
            Start with a 14-day free trial. No credit card required. Upgrade anytime as you grow.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={plan.name}
              className={`relative rounded-xl card-shadow-hover overflow-hidden bg-bg-primary animate-fade-in ${
                plan.popular
                  ? "border-2 border-brand-500"
                  : "border border-border-secondary"
              }`}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {plan.popular && (
                <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-brand-600 to-brand-400" />
              )}

              <div className="p-8">
                {plan.popular && (
                  <span className="inline-block text-xs font-semibold text-brand-600 bg-brand-50 px-2.5 py-1 rounded-full mb-4">
                    Most Popular
                  </span>
                )}

                <h3 className="text-xl font-bold text-text-primary mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm text-text-tertiary mb-6">
                  {plan.description}
                </p>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-text-primary">
                    {plan.price}
                  </span>
                  <span className="text-text-tertiary ml-2">
                    {plan.period}
                  </span>
                </div>

                <Link to="/login">
                  <Button
                    className="w-full mb-8"
                    color={plan.popular ? "primary" : "secondary"}
                  >
                    {plan.cta}
                  </Button>
                </Link>

                <ul className="space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <Check className="h-5 w-5 text-success-500 mr-3 shrink-0" />
                      <span className="text-sm text-text-secondary">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 text-sm text-text-tertiary text-center max-w-3xl mx-auto">
          All plans include free onboarding support. 14-day free trial on every plan, no credit card required. Prices in EUR, billed monthly.
        </p>
      </div>
    </section>
  );
}
